import { CustomComponent, JQueryPluginExtras, JQueryPluginFn, JQueryPluginPreInitFn } from '../common'

const CUSTOM_INPUT_ATTR = 'iw-custom-input';
const CUSTOM_INPUT_SELECTOR = `[${CUSTOM_INPUT_ATTR}]`;

/**
 * A base class that handles shared functionality for custom input components
 */
export abstract class CustomInputComponent<TElement extends HTMLElement = HTMLElement> extends CustomComponent<TElement> {

  public static readonly CUSTOM_INPUT_ATTR = CUSTOM_INPUT_ATTR;

  /**
   * Returns a selector string that can be used to find elements bound to a {@link CustomInputComponent} instance.
   *
   * The iw-custom-input attribute is automatically added to any component using {@link CustomInputComponent} so they
   * can be easily identified.
   */
  public static readonly CUSTOM_INPUT_SELECTOR = CUSTOM_INPUT_SELECTOR;

  /**
   * Overridden to return a CSS selector for the host element of the component.
   *
   * @abstract
   */
  public static COMPONENT_SELECTOR: string = '';

  public static jQueryPlugin<
    TExtras extends JQueryPluginExtras,
    TElement extends HTMLElement = HTMLElement
    >(controllerName: string, extras?: TExtras, preInit?: JQueryPluginPreInitFn<TElement>): JQueryPluginFn<TElement> & TExtras {
    return super.jQueryPlugin(controllerName, Object.assign({}, {
      CUSTOM_INPUT_ATTR: this.CUSTOM_INPUT_ATTR,
      CUSTOM_INPUT_SELECTOR: this.CUSTOM_INPUT_SELECTOR,
    }, extras), preInit);
  }

  public get hasFocus(): boolean {
    return this.$el.is(':focus,:focus-within');
  }

  public get required(): boolean {
    return this.$el.prop('required');
  }
  public set required(value: boolean) {
    this.$el.prop('required', value);
  }

  protected constructor($el: JQuery<TElement>) {
    super($el);

    this.$el.attr(CUSTOM_INPUT_ATTR, '');
  }

  /**
   * Returns a string representing the component host element.
   *
   * The string returned contains in order of preference:
   *   - A CSS selector style string with the tag name, "name" attribute and ID, if a "name" is present
   *     (e.g. iw-custom-input#some-id--1[name=the_input])
   *   - A CSS selector style string with the tag name ID
   *     (e.g. iw-custom-input#some-id--1
   */
  public getDebugIdentifier(): string {
    return this.getDebugIdentifierByName();
  }

  protected getDebugIdentifierByName(): string {
    const idDebug = this.getDebugIdentifierById();
    const name = this.$el.attr('name');
    if (name) {
      return `${idDebug}[name=${name}]`;
    }
    return idDebug;
  }
}
